import React, { useEffect, useRef, useState } from "react";
import { BadgeCheck, Star, Car, TrendingUp } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { useT } from "@/i18n";

// Small count-up animation for the social-proof sold counter.
function useCountUp(target, run) {
  const [val, setVal] = useState(0);
  const raf = useRef(0);
  useEffect(() => {
    const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!run || !target || reduce) { setVal(target || 0); return; }
    const start = performance.now();
    const dur = 900;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * target));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, run]);
  return val;
}

// Premium default banner shown when a dealer has no custom cover photo.
// Doubles as the promotional "showroom spotlight" ad. Supports a custom
// accent color and background pattern chosen by the dealer.
export default function ShowroomBanner({ name = "", logo, slogan, count = 0, verified = true, accent, pattern = "dots", soldMonth = 0, soldTotal = 0 }) {
  const { t } = useT();
  const initial = (name || "P").charAt(0).toUpperCase();
  const a = accent || "#123A63";
  const soldN = soldMonth > 0 ? soldMonth : soldTotal;
  const soldCount = useCountUp(soldN, true);

  const patternStyle = (() => {
    if (pattern === "none") return {};
    if (pattern === "grid") return { backgroundImage: "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)", backgroundSize: "32px 32px" };
    if (pattern === "mesh") return { backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 14px)" };
    if (pattern === "carbon") return { backgroundImage: "radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1px), radial-gradient(rgba(255,255,255,0.35) 1px, transparent 1px)", backgroundSize: "18px 18px", backgroundPosition: "0 0, 9px 9px" };
    return { backgroundImage: "radial-gradient(rgba(255,255,255,0.7) 1px, transparent 1px)", backgroundSize: "24px 24px" }; // dots
  })();

  return (
    <div className="showroom-banner relative h-full w-full overflow-hidden bg-[#0F2540]" data-testid="seller-default-banner">
      {/* base gradient tinted by accent */}
      <div className="absolute inset-0" style={{ background: `linear-gradient(115deg,#0F2540 0%, ${a} 55%, #2563eb 100%)` }} />
      {/* drifting glow orbs */}
      <div className="float-y absolute -right-16 -top-24 h-72 w-72 rounded-full blur-3xl" style={{ background: `${a}55` }} />
      <div className="absolute -bottom-28 left-10 h-72 w-72 rounded-full bg-sky-400/25 blur-3xl" />
      {/* pattern */}
      <div className="absolute inset-0 opacity-[0.14]" style={patternStyle} />
      {/* diagonal gloss + animated shine */}
      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.03] to-white/[0.12]" />
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="shine-sweep absolute -inset-y-8 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent" />
      </div>

      {/* right decorative medallion — premium emblem */}
      <div className="absolute right-5 top-1/2 hidden -translate-y-1/2 sm:block">
        <div className="relative h-44 w-44">
          {/* rotating dashed guide ring */}
          <svg className="spin-slow absolute inset-0" viewBox="0 0 176 176" fill="none">
            <circle cx="88" cy="88" r="83" stroke="rgba(255,255,255,0.18)" strokeWidth="1.5" strokeDasharray="3 10" />
          </svg>
          {/* conic gradient accent ring */}
          <div className="absolute inset-2 rounded-full p-[3px] shadow-[0_18px_50px_-12px_rgba(0,0,0,0.6)]"
            style={{ background: `conic-gradient(from 210deg, ${a}, #2563eb, #ffffff, ${a})` }}>
            <div className="relative h-full w-full overflow-hidden rounded-full bg-[#0F2540]/85 ring-1 ring-white/25 backdrop-blur-md">
              {/* glossy sheen */}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/30 via-transparent to-black/40" />
              <div className="pointer-events-none absolute inset-x-2 top-1 h-1/2 rounded-full bg-white/15 blur-md" />
              <div className="relative flex h-full w-full items-center justify-center">
                {logo
                  ? <img src={mediaUrl(logo)} alt="" className="h-24 w-24 rounded-full object-cover" />
                  : <span className="chrome-text font-display text-6xl font-black leading-none">{initial}</span>}
              </div>
            </div>
          </div>
          {verified && (
            <span className="absolute -bottom-1 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-white shadow-lg ring-2 ring-[#0F2540]">
              <BadgeCheck size={12} /> {t("common.verified")}
            </span>
          )}
        </div>
      </div>

      {/* left content */}
      <div className="relative z-10 flex h-full flex-col justify-center gap-2 px-6 sm:px-9">
        <div className="flex items-center gap-2">
          <img src="/brand/pactly-icon-white.png" alt="" className="h-8 w-auto object-contain" draggable={false} />
          <span className="font-display text-base font-extrabold leading-none text-white">Pactly<span className="text-sky-300"> Car</span></span>
          <span className="mx-1 h-4 w-px bg-white/25" />
          <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white ring-1 ring-white/20 backdrop-blur">
            <Star size={11} className="fill-amber-300 text-amber-300" /> {t("seller.official")}
          </span>
        </div>

        {/* chrome animated garage name */}
        <h2 className="chrome-text max-w-[88%] font-display text-3xl font-black uppercase leading-[1.05] tracking-tight sm:max-w-[72%] sm:text-[40px]">
          {name}
        </h2>

        <p className="max-w-[92%] text-sm font-medium text-white/75 sm:max-w-[65%]">
          {slogan || t("seller.bannerTagline")}
        </p>

        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-black/25 px-2.5 py-1 text-xs font-bold text-white ring-1 ring-white/15 backdrop-blur">
            <Car size={13} /> {count} {t("seller.listings")}
          </span>
          {soldN > 0 && (
            <span data-testid="banner-sold-counter" className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/90 px-2.5 py-1 text-xs font-extrabold text-white shadow-lg shadow-emerald-900/30 ring-1 ring-white/25 backdrop-blur">
              <TrendingUp size={13} /> {soldCount} {soldMonth > 0 ? t("seller.soldMonth") : t("seller.soldTotal")}
            </span>
          )}
          {verified && (
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-black/25 px-2.5 py-1 text-xs font-bold text-white ring-1 ring-white/15 backdrop-blur">
              <BadgeCheck size={13} className="text-emerald-300" /> {t("seller.proTrusted")}
            </span>
          )}
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-[#0F2540]/45 to-transparent" />
    </div>
  );
}
